import React, { useRef, useMemo, useCallback } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { googleMapsApiKey } from "config/envConfig";
import locationMarkerIcon from "assets/icons/location-marker.svg";
import dropMarkerIcon from "assets/icons/drop-off-location-marker.svg";
import { useOrderContext } from "contexts/OrderContext";
import { Box, CircularProgress, Typography } from "@mui/material";

const libraries = ["marker"]; // load Advanced Marker Element

const OrdersLocations = ({ currentDriver }) => {
  // create a ref to store GoogleMap instance
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
    language: "en",
  });

  const { orderDetails } = useOrderContext();

  const pickUpLocation = useMemo(() => {
    return orderDetails?.pickUpLocation?.coordinates
      ? {
          lat: +orderDetails.pickUpLocation.coordinates.latitude, //convert to numbers
          lng: +orderDetails.pickUpLocation.coordinates.longitude,
        }
      : null;
  }, [orderDetails?.pickUpLocation?.coordinates]);

  const dropOffLocation = useMemo(() => {
    return orderDetails?.dropOffLocation?.coordinates
      ? {
          lat: +orderDetails.dropOffLocation.coordinates.latitude,
          lng: +orderDetails.dropOffLocation.coordinates.longitude,
        }
      : null;
  }, [orderDetails?.dropOffLocation?.coordinates]);

  const mapOptions = useMemo(
    () => ({
      mapId: "mapId", // ask for it
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: "auto",
      zoomControl: true,
    }),
    []
  );

  const addAdvancedMarker = useCallback((position, iconUrl) => {
    if (
      !mapRef.current ||
      !window.google?.maps?.marker?.AdvancedMarkerElement
    ) {
      console.error("AdvancedMarkerElement or map is not ready.");
      return null;
    }

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      map: mapRef.current,
      content: iconUrl ? createMarkerHTML(iconUrl) : null,
    });

    return marker;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createMarkerHTML = useCallback((iconUrl) => {
    const markerDiv = document.createElement("div");
    markerDiv.style.width = "30px";
    markerDiv.style.height = "30px";
    markerDiv.style.backgroundImage = `url(${iconUrl})`;
    markerDiv.style.backgroundSize = "cover";
    return markerDiv;
  }, []);

  const handleMapLoad = useCallback(() => {
    if (pickUpLocation) addAdvancedMarker(pickUpLocation, locationMarkerIcon);
    if (dropOffLocation) addAdvancedMarker(dropOffLocation, dropMarkerIcon);
    if (currentDriver?.coordinates) {
      addAdvancedMarker(
        {
          lat: +currentDriver.coordinates.latitude,
          lng: +currentDriver.coordinates.longitude,
        },
        null
      );
    }
  }, [pickUpLocation, dropOffLocation, currentDriver, addAdvancedMarker]);

  if (loadError) {
    return <div>Error loading map: {loadError.message}</div>;
  }

  if (!isLoaded || !pickUpLocation || !dropOffLocation) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading map and data...
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={pickUpLocation || { lat: 0, lng: 0 }}
        zoom={14}
        options={mapOptions}
        onLoad={(map) => (mapRef.current = map)}
        onIdle={handleMapLoad} // once the map is ready add markers
      />
    </div>
  );
};

export default OrdersLocations;
